/* A small history router: enough for this app, no dependency.
   Patterns use ":param" segments, e.g. "/students/:id". */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface Loc { pathname: string; search: string }
interface RouterCtx { loc: Loc; navigate: (to: string, opts?: { replace?: boolean }) => void }

const Ctx = createContext<RouterCtx>({ loc: { pathname: '/', search: '' }, navigate: () => undefined });

function current(): Loc {
  return { pathname: window.location.pathname || '/', search: window.location.search };
}

let navigateRef: RouterCtx['navigate'] = (to) => { window.location.assign(to); };
/** Navigate from outside React (for example after a 401). */
export function navigate(to: string, opts?: { replace?: boolean }): void {
  navigateRef(to, opts);
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState<Loc>(current);
  useEffect(() => {
    const onPop = () => setLoc(current());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const value = useMemo<RouterCtx>(() => ({
    loc,
    navigate: (to, opts) => {
      if (opts?.replace) window.history.replaceState(null, '', to);
      else window.history.pushState(null, '', to);
      setLoc(current());
      window.scrollTo(0, 0);
    },
  }), [loc]);
  navigateRef = value.navigate;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRouter(): RouterCtx {
  return useContext(Ctx);
}

export function useQueryParams(): [URLSearchParams, (patch: Record<string, string | null | undefined>) => void] {
  const { loc, navigate: nav } = useRouter();
  const params = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const set = (patch: Record<string, string | null | undefined>) => {
    const next = new URLSearchParams(loc.search);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    }
    const s = next.toString();
    nav(loc.pathname + (s ? `?${s}` : ''), { replace: true });
  };
  return [params, set];
}

export function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = pathname.split('/').filter(Boolean);
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(':')) params[p[i].slice(1)] = decodeURIComponent(a[i]);
    else if (p[i] !== a[i]) return null;
  }
  return params;
}

export function Link({ to, children, className, activeClassName, exact, onClick, title }: {
  to: string; children: ReactNode; className?: string; activeClassName?: string; exact?: boolean; onClick?: () => void; title?: string;
}) {
  const { loc, navigate: nav } = useRouter();
  const path = to.split('?')[0];
  const active = exact ? loc.pathname === path : loc.pathname === path || loc.pathname.startsWith(path + '/');
  const cls = [className, active && activeClassName ? activeClassName : ''].filter(Boolean).join(' ') || undefined;
  return (
    <a href={to} className={cls} title={title} aria-current={active ? 'page' : undefined}
      onClick={(e) => {
        const ev = e.nativeEvent as MouseEvent;
        if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button !== 0) return;
        e.preventDefault();
        onClick?.();
        nav(to);
      }}>
      {children}
    </a>
  );
}
